/**
 * Has a "connected" bank silently stopped refreshing? (T-505)
 *
 * An aggregator can stop refreshing a bank's transactions WITHOUT reporting an error — Plaid leaves
 * `item.error` null, so `c_aggregator_connected` stays true, and our own pull keeps succeeding against
 * the frozen data. Found 2026-10-07 on five Plaid items (BMO, CIT, Topside, Huntington, Capital One),
 * every one a real problem and none flagged anywhere. This module is the ONE rule for "stale": the
 * admin SPA and the mobile NOT UPDATING state read it, and T-506's daily alert is to read it too, so they
 * cannot disagree about which banks are stuck.
 *
 * ## Staleness is measured AT OUR LAST PULL, not against now
 *
 * `c_last_refresh_from_institution_to_aggregator` only moves when WE pull, so for a membership we have
 * stopped pulling (a deleted or lapsed owner) it ages for reasons that have nothing to do with the
 * bank. On prod 2026-10-08 a naive `now − refresh > 5d` rule flags 64 Plaid / 4 MX / 6 Quiltt
 * memberships; measuring the gap at our last pull flags 5 / 0 / 0 — exactly the real problems. So the
 * question asked is "was the bank already more than 5 days behind the last time we looked?", which is
 * a fact about the BANK no matter how long ago that look was. The day count reported to a user is
 * still against now, because it describes how old their data is.
 *
 * ## The 5-day threshold
 *
 * From T-506's 90-day replay of sync history: a 72h rule is dominated by banks that are slow but
 * working (Schools First FCU, Wells Fargo, Synchrony store cards, Marcus) — telling those users
 * "your bank isn't sending updates" would be false. 5 days is where the replay turns into real
 * problems. T-506's daily alert imports the same constant; do not restate it.
 *
 * ## Elapsed time, not calendar days
 *
 * Both sides of the gap are instants written by our own sync, so elapsed milliseconds are the honest
 * measure; a calendar-day rule would make the boundary depend on the time of day of each sync.
 *
 * Pure, Hermes-safe (no arktype, no Node APIs), `RooDate`-only.
 */
import { RooDate } from "roo-time";
import { MS_PER_DAY } from "./constants.js";
/** A bank more than this many days behind (at our last pull) is stale. Shared with T-506's alert. */
export const STALE_INSTITUTION_REFRESH_DAYS = 5;
/**
 * Any refresh or pull date before this is a placeholder, not a time. Two are known: the server's own
 * `DATE_FOR_SYNC_HASNT_HAPPENED` (1900-01-01) and an epoch-0 (1970) value seen on a SoFi membership.
 * Classifying either as stale would tell a user their bank stopped 126 years ago.
 */
export const INSTITUTION_REFRESH_PLACEHOLDER_FLOOR = new RooDate("2000-01-01T00:00:00.000Z");
/**
 * Is this a real instant rather than a missing, Invalid, or placeholder date? The placeholder rule for any
 * caller that renders one of these dates on its own — e.g. a disconnected membership's "1/1/1900", which the
 * classifier never looks at — so the floor isn't restated per surface.
 */
export function isUsableInstitutionRefreshDate(date) {
    if (!date)
        return false;
    const ms = date.getTime();
    return Number.isFinite(ms) && ms >= INSTITUTION_REFRESH_PLACEHOLDER_FLOOR.getTime();
}
export function classifyInstitutionRefresh(p) {
    const m = p.membership;
    if (m.archived_at || !m.c_aggregator_connected || m.aggregator_source === "MANUAL") {
        return { kind: "not-applicable" };
    }
    const lastRefreshAt = m.c_last_refresh_from_institution_to_aggregator;
    const lastPullAt = m.last_sync_from_aggregator_to_app;
    if (!isUsableInstitutionRefreshDate(lastRefreshAt) || !isUsableInstitutionRefreshDate(lastPullAt)) {
        return { kind: "unknown" };
    }
    const gapAtLastPullMs = lastPullAt.getTime() - lastRefreshAt.getTime();
    if (gapAtLastPullMs <= STALE_INSTITUTION_REFRESH_DAYS * MS_PER_DAY) {
        return { kind: "fresh" };
    }
    return {
        kind: "stale",
        // Against now, but never from before our last pull: `nowMs` is the caller's clock while the pull time is
        // the server's, so a device clock running behind would count fewer days than the gap that made this
        // stale — "No new transactions in 0 days", even a negative count, beside a NOT UPDATING badge.
        daysSinceRefresh: Math.floor((Math.max(p.nowMs, lastPullAt.getTime()) - lastRefreshAt.getTime()) / MS_PER_DAY),
        lastRefreshAt,
        attempt: classifyAttempt({
            attemptAt: m.c_last_attempted_refresh_from_institution_to_aggregator,
            lastRefreshAt,
            lastPullAt,
        }),
    };
}
/**
 * The attempt half of a stale verdict. ⚠ "Recent" must stay bounded: without it, one failure the day
 * after the last success read as "still trying" forever, contradicting "both old ⇔ it isn't trying"
 * (PR #1887 review #1). The bound reuses the stale threshold rather than a separate number, because how
 * often Plaid's `last_failed_update` moves has not been measured yet — tighten it from T-506's replay
 * once the column has prod data, not by guessing.
 */
function classifyAttempt(p) {
    if (!isUsableInstitutionRefreshDate(p.attemptAt)) {
        return { kind: "unknown" };
    }
    const attemptedAfterLastSuccess = p.attemptAt.getTime() > p.lastRefreshAt.getTime();
    const recentAtLastPull = p.lastPullAt.getTime() - p.attemptAt.getTime() <= STALE_INSTITUTION_REFRESH_DAYS * MS_PER_DAY;
    return attemptedAfterLastSuccess && recentAtLastPull
        ? { kind: "trying", at: p.attemptAt }
        : { kind: "stopped", at: p.attemptAt };
}
//# sourceMappingURL=institutionRefreshStaleness.js.map